import {
  CommunicationStatus,
  disabled,
  getCommunicationStatusDetails,
  HttpOrCommunicationStatus,
  isClientFailure,
  isClientFailureNotAuthorized,
  isFailure,
  isMissing,
  isPending,
  isServerFailure,
  isSuccess,
  pending,
  success,
} from '@art-suite/art-core-ts';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { ErrorWithStatus, getErrorCommunicationStatus } from '@kaiizen/zod-less-api';

import { isCacheEntryFresh, TypedCacheEntry, usePersistentCachedEntry } from '../PersistentCache';
import { RequestDataFn, useQueryCache } from './QueryCacheProvider';
import { getDerivedStatus } from './queryResultLib';
import { UseCachedQueryOptions, UseCachedQueryResult } from './types';

/**
 * Internal state stored by the hook.
 * We store only the minimal state and derive the rest.
 */
type QueryState = {
  status: CommunicationStatus;
  progress: number;
  isFetching: boolean;
  error?: Error | undefined;
};

const getFriendlyErrorMessage = (communicationStatus: HttpOrCommunicationStatus): string => {
  if (isSuccess(communicationStatus)) return '';
  if (isMissing(communicationStatus)) return 'Not found';
  if (isClientFailureNotAuthorized(communicationStatus)) return 'Not authorized';
  if (isClientFailure(communicationStatus)) return 'Client failure';
  if (isServerFailure(communicationStatus)) return 'Server failure';
  if (isFailure(communicationStatus)) return 'Failure';
  if (isPending(communicationStatus)) return 'Pending';
  return getCommunicationStatusDetails(communicationStatus).message;
};

/**
 * Base hook for caching and retrieving data from the query cache.
 *
 * React Query-compatible semantics:
 * - status: 'pending' (fetching, no data), 'success' (has data), 'error' (failed, no data)
 * - isFetching: true whenever any request is in flight
 * - isRefetching: true when fetching but already have data
 * - isLoading: true only when status === 'pending'
 *
 * @param key - The key to retrieve the data from.
 * @param fetchFunction - The function to fetch the data.
 * @param options - The options for the query.
 * @returns The query result.
 */
export const useCachedQuery = <T>(
  key: string,
  fetchFunction: RequestDataFn<T>,
  options: UseCachedQueryOptions<T> = {},
): UseCachedQueryResult<T> => {
  const queryCache = useQueryCache<T>();
  const {
    preloadData,
    enabled = true,
    prefetchOnly = false,
    staleAgeMs = queryCache.defaultStaleAgeMs,
    maxAgeMs = queryCache.defaultMaxAgeMs,
    maxRetryMs = queryCache.defaultMaxRetryMs,
    retryDelayMs = queryCache.defaultRetryDelayMs,
    maxRetryDelayMs = queryCache.defaultMaxRetryDelayMs,
    minTtlOnAccessMs,
    priority = queryCache.defaultPriority,
    retryServerFailures = true,
    onSuccess,
    onError,
  } = options;
  const isMounted = useRef(true);
  const fetchingPromiseRef = useRef<Promise<void> | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  // preload the data if it was provided FIRST - then usePersistentCachedEntry will return the preloaded data
  useEffect(() => {
    if (preloadData) {
      queryCache.cache.set(key, preloadData, Date.now() + maxAgeMs);
    }
  }, [preloadData, key, maxAgeMs, queryCache.cache]);

  const persistentCachedEntry = usePersistentCachedEntry<T>(key, { prefetchOnly, minTtlOnAccessMs });

  const requestFnRef = useRef(fetchFunction);
  requestFnRef.current = fetchFunction;
  const onSuccessRef = useRef(onSuccess);
  onSuccessRef.current = onSuccess;
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;
  const queryCacheRef = useRef(queryCache);
  queryCacheRef.current = queryCache;

  const [queryState, setQueryState] = useState<QueryState>(() => ({
    status: persistentCachedEntry ? success : disabled,
    progress: 0,
    isFetching: false,
  }));

  const fetchData = useCallback((): Promise<void> => {
    if (!enabled) return Promise.resolve();
    // Single-flight gate: hand any concurrent caller (polling timer, user
    // refetch, initial load) the in-flight promise so they all resolve when the
    // actual fetch settles — not before.
    if (fetchingPromiseRef.current) return fetchingPromiseRef.current;

    const fetchFunction = requestFnRef.current;
    const queryCache = queryCacheRef.current;

    if (queryCache.isOffline) return Promise.resolve();

    // Fresh AbortController per fetch. Aborts when the hook unmounts, key
    // changes, or enabled flips false — see the cleanup in the main effect.
    const controller = new AbortController();
    abortControllerRef.current = controller;

    setQueryState(prev => ({
      ...prev,
      isFetching: true,
      status: prev.status === success ? success : pending,
    }));

    const promise = queryCache.requestData<T>(
      key,
      fetchFunction,
      { maxRetryMs, retryDelayMs, maxAgeMs, priority, prefetchOnly, maxRetryDelayMs, retryServerFailures },
      {
        setProgress: (progress: number) => setQueryState(prev => ({ ...prev, progress })),
        signal: controller.signal,
      },
    ).then(
      cacheEntry => {
        // signal.aborted: the abandoned-fetch case (unmount, key change,
        // enabled→false). Cleanup fires controller.abort() so we know to
        // drop the result silently rather than paint stale state.
        if (controller.signal.aborted || !isMounted.current) return;
        setQueryState({ status: success, isFetching: false, progress: 1 });
        onSuccessRef.current?.(cacheEntry.value);
      },
      _error => {
        // Abort rejections are intentional cancellations, not failures —
        // drop them silently so an aborted poll doesn't paint an error
        // state or fire onError.
        if (controller.signal.aborted || !isMounted.current) return;
        const error = _error instanceof Error ? _error : new Error(String(_error));

        setQueryState(prev => ({
          // React Query semantics: keep status='success' if we have data (refetch failed but stale data is still valid)
          status: prev.status === success ? success : getErrorCommunicationStatus(error),
          isFetching: false,
          error,
          progress: 1,
        }));
        onErrorRef.current?.(error);
      },
    ).finally(() => {
      // Only clear the refs if they still point at THIS fetch — a key
      // change can spawn a new fetch before this one settles, and we
      // mustn't clobber the new fetch's bookkeeping.
      if (fetchingPromiseRef.current === promise) fetchingPromiseRef.current = null;
      if (abortControllerRef.current === controller) abortControllerRef.current = null;
    });

    fetchingPromiseRef.current = promise;
    return promise;
  }, [enabled, key, maxAgeMs, maxRetryMs, retryDelayMs, priority, prefetchOnly, setQueryState, onSuccessRef, onErrorRef, maxRetryDelayMs, retryServerFailures]);

  const fetchDataRef = useRef(fetchData);
  fetchDataRef.current = fetchData;

  const haveData = !!persistentCachedEntry?.value;
  const prevHaveDataRef = useRef(haveData);
  // If the persistent cached entry was cleared (e.g. purged) while we still
  // have an active subscriber, refetch. Skip on initial mount — initialDataLoad
  // below already handles the "no data yet" case.
  useEffect(() => {
    if (prevHaveDataRef.current && !haveData) fetchData();
    prevHaveDataRef.current = haveData;
  }, [fetchData, haveData]);

  useEffect(() => {
    isMounted.current = true;

    if (!enabled) return;

    const queryCache = queryCacheRef.current;
    const fetchData = fetchDataRef.current;
    const refetchWrapper = () => fetchDataRef.current();

    const unregisterRefresh = prefetchOnly
      ? queryCache._registerMetadataRefetch(key, refetchWrapper)
      : queryCache._registerRefetch(key, refetchWrapper);

    const initialDataLoad = async () => {
      try {
        const cacheEntry = (
          prefetchOnly
            ? await queryCache.cache.getMetadata(key)
            : await queryCache.cache.getEntry(key)
        ) as TypedCacheEntry<T> | undefined;

        if (!isMounted.current) return;

        if (cacheEntry) {
          setQueryState({ status: success, isFetching: false, progress: 1 });
          if (!isCacheEntryFresh(cacheEntry, staleAgeMs)) fetchData();
        } else fetchData();
      } catch (_error) {
        if (!isMounted.current) return;
        const error = _error instanceof Error ? _error : new Error(String(_error));
        console.error('useCachedQuery: internal error', error);
        setQueryState({ error, status: getErrorCommunicationStatus(error), isFetching: false, progress: 1 });
        onErrorRef.current?.(error);
      }
    };

    if (!preloadData) initialDataLoad();

    return () => {
      isMounted.current = false;
      // Abort any in-flight fetch — fires on real unmount, key change, or
      // enabled flipping false. Long-running fetch functions (e.g. job
      // pollers) observe this via requestDataFnOptions.signal and bail out.
      abortControllerRef.current?.abort();
      abortControllerRef.current = null;
      fetchingPromiseRef.current = null;
      unregisterRefresh();
    };
  }, [key, enabled, staleAgeMs, prefetchOnly, preloadData]);

  const data = prefetchOnly ? undefined : persistentCachedEntry?.value ?? preloadData;

  // isLoading = isPending && isFetching (only true when fetching with no data)
  const { status, isFetching, error } = queryState;
  const isReady = prefetchOnly ? !!persistentCachedEntry : !!data;
  const isEnabled = status !== disabled && enabled;
  const errorInfo = !error ? undefined : (error instanceof ErrorWithStatus ? getFriendlyErrorMessage(error.status) : error.message);

  return {
    ...queryState,
    ...getDerivedStatus(isReady, isFetching, isEnabled, status),
    data,
    error,
    errorInfo,
    key,
    refetch: fetchData,
    ...useMemo(() => ({
      getCurrent: () => queryCache.cache.getEntry(key).then(entry => entry?.value),
      getCurrentMetadata: () => queryCache.cache.getMetadata(key),
      update: (data: T) => queryCache.cache.set(key, data, Date.now() + maxAgeMs),
    }), [key, queryCache.cache, maxAgeMs]),
    updatedAt: persistentCachedEntry?.createdAt,
  };
};
